import {createUseStyles} from 'react-jss'
import {useNavigate} from 'react-router-dom'
import {Colors} from '../../constants/colors'
import {EmployeeListItemModel} from '../../redux/types'
import Pagination from '../Pagination'
import {Breakpoints} from "../../constants/breakpoints";
import {ListItemColumnDefinition} from "./ListItemColumnDefinition";

export interface Props {
  employeesOnPage?: EmployeeListItemModel[]
  pageIndex: number
  pageSize: number
  totalCount: number
  onPageChange: (pageIndex: number) => void
  columns: ListItemColumnDefinition[]
}

const EmployeeList = ({employeesOnPage, pageIndex, pageSize, totalCount, onPageChange, columns}: Props) => {
  const styles = useStyles()
  const navigate = useNavigate()

  const renderHeader = () => {
    return (
      <thead>
      <tr>
        {columns.map((col, index) => (
          <th className={styles.headerCell} key={`header_${index}`}>{col.title}</th>
        ))}
      </tr>
      </thead>
    )
  }

  const renderListItems = () => {
    const getColumnStyle = (col: ListItemColumnDefinition) => {
      if (col.style === "Badge") {
        return styles.badge
      }
    }

    return (
      <tbody>
      {employeesOnPage?.map((item, index) => (
        <tr
          key={`employee_${index}`}
          className={styles.row}
          onClick={() => navigate(`/employees/${item?.id}`)}
        >
          {columns.map((col, colIndex) => (
            <td className={styles.cell} key={`item_${index}_${colIndex}`}>
              <span className={styles.cellHeader}>{col.title}: </span>
              <span className={getColumnStyle(col)}>
                {col.extractor(item)}
              </span>
            </td>
          ))}
        </tr>
      ))}
      </tbody>
    )
  }

  return (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        {renderHeader()}
        {renderListItems()}
      </table>
      <div className={styles.pagination}>
        <Pagination
          pageIndex={pageIndex}
          itemCount={totalCount}
          pageSize={pageSize}
          siblingCount={1}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  )
}

export default EmployeeList

const useStyles = createUseStyles({
  tableContainer: {
    padding: '10px',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontSize: '14px',
  },
  row: {
    cursor: 'pointer',
    transition: '0.4s',
    '&:hover': {
      backgroundColor: Colors.darkGrayHover,
    }
  },
  cell: {
    padding: '2px 12px',
    textAlign: 'left',
    '&:first-child': {
      borderTopLeftRadius: '6px',
      borderBottomLeftRadius: '6px',
    },
    '&:last-child': {
      borderTopRightRadius: '6px',
      borderBottomRightRadius: '6px',
    },
  },
  cellHeader: {
    display: 'none'
  },
  headerCell: {
    padding: '6px 12px',
    textAlign: 'left',
    fontFamily: 'Aestetico-Bold'
  },
  badge: {
    textAlign: 'center',
    textTransform: 'capitalize',
    borderRadius: 8,
    padding: '1px 4px',
    backgroundColor: Colors.darkGray3,
    fontFamily: 'Aestetico-Regular',
    fontSize: 13,
    color: Colors.darkGray4,
  },
  pagination: {
    marginTop: 10,
    display: 'flex',
    justifyContent: 'center'
  },
  [`@media (max-width: ${Breakpoints.desktop}px)`]: {
    headerCell: {
      display: 'none'
    },
    cellHeader: {
      display: 'inline',
      fontWeight: 'bold'
    },
    cell: {
      display: 'block'
    },
    row: {
      display: 'block',
      marginBottom: '10px',
      border: `2px solid ${Colors.darkGrayBorder}`,
      borderRadius: 10,
    }
  }
})
